import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseLocalDate } from '../utils/dates';
import { useFacility } from '../contexts/FacilityContext';
import FacilityFilter from '../components/FacilityFilter';
import ActiveFacilityBanner from '../components/ActiveFacilityBanner';
import { fetchClaims } from '../services/api';
import './Claims.css';

const STATUS_CONFIG = {
  pending: { color: '#f59e0b', bg: '#fffbeb', label: 'Pending' },
  submitted: { color: '#3b82f6', bg: '#eff6ff', label: 'Submitted' },
  denied: { color: '#ef4444', bg: '#fef2f2', label: 'Denied' },
  paid: { color: '#05998d', bg: '#f0fdfa', label: 'Paid' },
};

// Lifecycle / clearinghouse status — distinct from payment status above.
const CLAIM_STATUS_CONFIG = {
  ready:     { color: '#6b7280', bg: '#f3f4f6', label: 'Ready' },
  generated: { color: '#6366f1', bg: '#eef2ff', label: 'Generated' },
  pending:   { color: '#0891b2', bg: '#ecfeff', label: 'Pending' },
  accepted:  { color: '#05998d', bg: '#f0fdfa', label: 'Accepted' },
  rejected:  { color: '#ef4444', bg: '#fef2f2', label: 'Rejected' },
  voided:    { color: '#475569', bg: '#f1f5f9', label: 'Voided' },
  void_pending:    { color: '#475569', bg: '#f1f5f9', label: 'Void Pending' },
};

function Claims() {
  const navigate = useNavigate();
  const { selectedFacility, facilities } = useFacility();
  const [manualFilter, setManualFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const facilityId  = selectedFacility?.id ?? null;
  const facilityIds = !selectedFacility && manualFilter.length > 0 ? manualFilter : null;

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchClaims(facilityId, facilityIds)
      .then((res) => setClaims(res.data))
      .catch(() => setError('Failed to load claims. Please try again.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, manualFilter.join(',')]);

  const getFacilityName = (facilityId) =>
    facilities.find((f) => f.facility_id === facilityId || f.id === facilityId)?.name || 'Unknown';

  const filteredClaims = useMemo(() => {
    let list = claims;

    if (statusFilter !== 'all') {
      list = list.filter((c) => c.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (c) =>
          c.claim_number.toLowerCase().includes(q) ||
          (c.patient_name || '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [claims, searchQuery, statusFilter]);

  const totalAmount = filteredClaims.reduce((sum, c) => sum + c.amount, 0);

  const statusCounts = useMemo(() => {
    const counts = { pending: 0, submitted: 0, denied: 0, paid: 0 };
    claims.forEach((c) => { counts[c.status]++; });
    return counts;
  }, [claims]);

  return (
    <div className="claims-page">
      <ActiveFacilityBanner />
      <div className="claims-header">
        <div>
          <h1 className="claims-title">Claims Ledger</h1>
          <p className="claims-subtitle">
            {selectedFacility ? `Complete record of all claims for ${selectedFacility.name}` : 'Complete record of all claims across all facilities'}
          </p>
        </div>
        {!selectedFacility && (
          <FacilityFilter
            selectedIds={manualFilter}
            onChange={setManualFilter}
          />
        )}
      </div>

      {/* Status Summary */}
      <div className="claims-status-bar">
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <button
            key={key}
            className={`status-chip ${statusFilter === key ? 'active' : ''}`}
            style={{
              '--chip-color': config.color,
              '--chip-bg': config.bg,
            }}
            onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
          >
            <span className="status-chip-dot" />
            <span className="status-chip-count">{statusCounts[key]}</span>
            <span className="status-chip-label">{config.label}</span>
          </button>
        ))}
      </div>

      {/* Search + Stats */}
      <div className="claims-toolbar">
        <div className="claims-search-wrapper">
          <svg className="claims-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="claims-search"
            placeholder="Search by claim number or patient name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="claims-search-clear" onClick={() => setSearchQuery('')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <div className="claims-toolbar-right">
          <span className="claims-count">{filteredClaims.length} claims</span>
          <span className="claims-total">${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>
      </div>

      {error && <div className="claims-error">{error}</div>}

      {/* Table */}
      <div className="claims-table-card">
        {loading ? (
          <div className="claims-empty"><p>Loading claims…</p></div>
        ) : filteredClaims.length === 0 ? (
          <div className="claims-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <h3>No claims found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          <table className="claims-table">
            <thead>
              <tr>
                <th>Claim #</th>
                <th>Patient</th>
                <th>Service Date</th>
                <th className="num">Amount</th>
                <th>Claim Status</th>
                <th>Payment Status</th>
                <th>Facility</th>
              </tr>
            </thead>
            <tbody>
              {filteredClaims.map((claim) => {
                const sc = STATUS_CONFIG[claim.status];
                const cc = CLAIM_STATUS_CONFIG[claim.claim_status];
                return (
                  <tr key={claim.id} onClick={() => navigate(`/claims/${claim.id}`)} style={{ cursor: 'pointer' }}>
                    <td className="claim-number-cell">
                      {claim.claim_number}
                      {claim.cob_order > 1 && (
                        <span className="cob-order-badge">
                          {claim.cob_order === 2 ? 'Secondary' : 'Tertiary'} COB
                        </span>
                      )}
                    </td>
                    <td>{claim.patient_name}</td>
                    <td>{parseLocalDate(claim.service_date).toLocaleDateString()}</td>
                    <td className="claim-amount">${claim.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td>
                      {cc ? (
                        <span
                          className="claim-status-badge"
                          style={{ color: cc.color, backgroundColor: cc.bg }}
                        >
                          {cc.label}
                        </span>
                      ) : (
                        <span className="claim-status-empty">—</span>
                      )}
                    </td>
                    <td>
                      {sc ? (
                        <span
                          className="claim-status-badge"
                          style={{ color: sc.color, backgroundColor: sc.bg }}
                        >
                          {sc.label}
                        </span>
                      ) : (
                        <span className="claim-status-empty">—</span>
                      )}
                    </td>
                    <td className="facility-cell">{getFacilityName(claim.facility_id)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Claims;
